/**
 * RFC 7807 Problem Details — what ASP.NET Core returns for failed requests.
 */
export interface ProblemDetails {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    instance?: string;
    errors?: Record<string, string[]>;
    traceId?: string;
    [key: string]: unknown;
}

export class ApiError extends Error {
    readonly status: number;
    readonly title: string;
    readonly detail?: string;
    readonly validationErrors?: Record<string, string[]>;
    readonly problemDetails?: ProblemDetails;
    readonly traceId?: string;

    constructor(
        status: number,
        title: string,
        options?: {
            detail?: string;
            validationErrors?: Record<string, string[]>;
            problemDetails?: ProblemDetails;
            traceId?: string;
        }
    ) {
        super(options?.detail ?? title);
        this.name = 'ApiError';
        this.status = status;
        this.title = title;
        this.detail = options?.detail;
        this.validationErrors = options?.validationErrors;
        this.problemDetails = options?.problemDetails;
        this.traceId = options?.traceId;

        if ('captureStackTrace' in Error) {
            (Error as { captureStackTrace: (e: Error, c: Function) => void })
                .captureStackTrace(this, ApiError);
        }
    }

    get isValidationError(): boolean {
        return this.status === 400
            && !!this.validationErrors
            && Object.keys(this.validationErrors).length > 0;
    }

    get isClientError(): boolean { return this.status >= 400 && this.status < 500; }
    get isServerError(): boolean { return this.status >= 500; }

    get allValidationMessages(): string[] {
        if (!this.validationErrors) return [];
        return Object.values(this.validationErrors).flat();
    }

    getUserMessage(): string {
        if (this.isValidationError) {
            const messages = this.allValidationMessages;
            if (messages.length === 1) return messages[0];
            if (messages.length > 1) return `${messages[0]} (+${messages.length - 1} more)`;
        }
        return this.detail ?? this.title;
    }

    static async fromResponse(response: Response): Promise<ApiError> {
        const status = response.status;
        let problemDetails: ProblemDetails | undefined;

        try {
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json') || contentType?.includes('application/problem+json')) {
                problemDetails = await response.json();
            }
        } catch {
            // Non-JSON body — fall through.
        }

        if (problemDetails) {
            return new ApiError(status, problemDetails.title ?? defaultTitle(status), {
                detail: problemDetails.detail,
                validationErrors: problemDetails.errors,
                problemDetails,
                traceId: problemDetails.traceId
            });
        }

        return new ApiError(status, defaultTitle(status));
    }
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

function defaultTitle(status: number): string {
    switch (status) {
        case 400: return 'Bad Request';
        case 401: return 'Unauthorized';
        case 403: return 'Forbidden';
        case 404: return 'Not Found';
        case 409: return 'Conflict';
        case 422: return 'Validation Error';
        case 429: return 'Too Many Requests';
        case 500: return 'Internal Server Error';
        case 502: return 'Bad Gateway';
        case 503: return 'Service Unavailable';
        default:
            if (status >= 400 && status < 500) return 'Request Error';
            if (status >= 500) return 'Server Error';
            return 'Unknown Error';
    }
}
